import type {
  ApiKeyUsageAggregatedItem,
  ApiKeyUsageDetailResponse,
  ApiKeyUsageSummary,
  UsageAggregatedParams,
  UsageDetailsFilter,
  UsageSummaryParams,
} from "@/schemas/api-keys-usage.schema";
import type { ApiKey, ApiKeyBody, RotateApiKeyBody } from "@/schemas/api-keys.schema";
import type { RequestConfig } from "@/services/api";
import { api } from "@/services/api";
import { createLoopbackCrud } from "@/services/crud";

const baseCrud = createLoopbackCrud<ApiKey, ApiKeyBody>("api-keys");

export const apiKeysApi = {
  ...baseCrud,

  /**
   * Delete API key by ID with optional config (for project headers)
   */
  async deleteById(id: string, config?: RequestConfig) {
    await api.delete(`/api-keys/${id}`, config);
  },

  /**
   * Rotate an API key credential.
   *
   * Backend endpoint: POST /api-keys/{id}/rotate
   *
   * The returned key carries the new plaintext exactly once. The existing key
   * record (id/name/owner/org/project/created_at/billing) is preserved.
   * `grace_period_hours` is request input only.
   */
  async rotate(payload: { id: string } & RotateApiKeyBody, config?: RequestConfig) {
    const { id, ...body } = payload;
    const { data } = await api.post<ApiKey>(`/api-keys/${id}/rotate`, body, config);
    return data;
  },

  /**
   * End a rotation grace period early.
   *
   * Backend endpoint: POST /api-keys/{id}/finish-rotation - returns no new
   * secret, the current credential stays the same.
   */
  async finishRotation(id: string, config?: RequestConfig) {
    await api.post(`/api-keys/${id}/finish-rotation`, {}, config);
  },

  /**
   * Get usage summary for an API key
   *
   * Backend endpoint: GET /api-keys/{id}/usage/summary
   *
   * @param apiKeyId - API Key ID
   * @param params - Query parameters (start_date?, end_date?)
   * @returns Usage summary with total cost, requests, and period info
   *
   * @example
   * ```ts
   * const summary = await apiKeysApi.getUsageSummary('key-123', {
   *   start_date: '2024-01-01',
   *   end_date: '2024-01-31'
   * });
   * ```
   */
  /**
   * Get all API keys visible to the user within their organization.
   * Organization-scope users see all org keys; others see only their own.
   */
  async getOrganizationKeys(config?: RequestConfig) {
    const { data } = await api.get<ApiKey[]>("/api-keys", {
      ...config,
      params: { ...config?.params, scope: "organization" },
    });
    return data;
  },

  /**
   * Get API keys for a specific user (admin only)
   */
  async getForUser(userId: string, config?: RequestConfig) {
    const { data } = await api.get<ApiKey[]>("/api-keys", {
      ...config,
      params: { ...config?.params, target_user_id: userId },
    });
    return data;
  },

  /**
   * Create API key on behalf of another user (admin only)
   */
  async createForUser(userId: string, payload: ApiKeyBody, config?: RequestConfig) {
    const { data } = await api.post<ApiKey>("/api-keys", payload, {
      ...config,
      params: { ...config?.params, for_user_id: userId },
    });
    return data;
  },

  async getUsageSummaryById(apiKeyId: string, params?: UsageSummaryParams) {
    const { data } = await api.get<ApiKeyUsageSummary>(`/api-keys/${apiKeyId}/usage/summary`, {
      params,
    });
    return data;
  },

  async getUsageSummary(params?: UsageSummaryParams) {
    const { data } = await api.get<
      (Omit<ApiKeyUsageSummary, "api_key_id" | "api_key_name"> & { id: string })[]
    >(`/api-keys/usage/summary`, {
      params,
    });
    return data;
  },

  /**
   * Get paginated usage details for an API key
   *
   * Backend endpoint: GET /api-keys/{id}/usage/details
   * Uses Loopback Filter format for pagination and filtering
   *
   * @param apiKeyId - API Key ID
   * @param filter - Loopback filter object (limit, skip, where, order, include)
   * @returns Paginated usage details with count
   *
   * @example
   * ```ts
   * const details = await apiKeysApi.getUsageDetails('key-123', {
   *   limit: 50,
   *   skip: 0,
   *   order: ['event_timestamp DESC'],
   *   include: [{ relation: 'items' }]
   * });
   * ```
   */
  async getUsageDetailsById(apiKeyId: string, filter?: UsageDetailsFilter) {
    const { data } = await api.get<ApiKeyUsageDetailResponse>(
      `/api-keys/${apiKeyId}/usage/details`,
      {
        params: { filter: JSON.stringify(filter) },
      }
    );
    return data;
  },

  /**
   * Get aggregated usage data for an API key
   *
   * Backend endpoint: GET /api-keys/{id}/usage/aggregated
   *
   * @param apiKeyId - API Key ID
   * @param params - Aggregation parameters
   *   - start_date?: Start date (ISO string)
   *   - end_date?: End date (ISO string)
   *   - group_by?: Comma-separated: "period", "type", "identifier"
   *   - period_unit?: "day" | "week" | "month" (when grouping by period)
   * @returns Array of aggregated usage items
   *
   * @example
   * ```ts
   * // Aggregate by period (daily)
   * const aggregated = await apiKeysApi.getUsageAggregated('key-123', {
   *   start_date: '2024-01-01',
   *   end_date: '2024-01-31',
   *   group_by: 'period',
   *   period_unit: 'day'
   * });
   *
   * // Aggregate by type and period
   * const aggregated = await apiKeysApi.getUsageAggregated('key-123', {
   *   group_by: 'period,type',
   *   period_unit: 'week'
   * });
   * ```
   */
  async getUsageAggregated(apiKeyId: string, params?: UsageAggregatedParams) {
    const { data } = await api.get<ApiKeyUsageAggregatedItem[]>(
      `/api-keys/${apiKeyId}/usage/aggregated`,
      {
        params,
      }
    );
    return data;
  },
};
